import { browserSupportsWebAuthn, startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, ApiRequestError } from "../data/api";
import { Avatar } from "../ui/Avatar";
import { Wordmark } from "../ui/Brand";
import { Icon } from "../ui/Icon";
import { Scenery } from "./Scenery";

// Passkeys (worker/routes/passkeys.ts): family members sign in with Face ID,
// a fingerprint or the phone's screen lock instead of a Google account.

/** WebAuthn errors read as plain words; cancelling the phone's prompt isn't an error at all. */
function passkeyError(e: unknown): string | null {
  const name = (e as { name?: string })?.name;
  if (name === "NotAllowedError" || name === "AbortError") return null;
  if (name === "InvalidStateError") return "This phone already has a passkey for Kinmantle. Use “Sign in with a passkey”.";
  if (e instanceof ApiRequestError) return e.message;
  return (e as Error)?.message || "Something went wrong. Please try again.";
}

/** "Sign in with a passkey" on the sign-in screen. */
export function PasskeySignIn({ returnTo }: { returnTo: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!browserSupportsWebAuthn()) return null;
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      const { challengeId, options } = await api<{ challengeId: string; options: Parameters<typeof startAuthentication>[0]["optionsJSON"] }>("/auth/passkey/login/options", { method: "POST", body: {} });
      const response = await startAuthentication({ optionsJSON: options });
      await api("/auth/passkey/login/verify", { method: "POST", body: { challengeId, response } });
      window.location.replace(returnTo);
    } catch (e) {
      setError(passkeyError(e));
      setBusy(false);
    }
  };
  return (
    <>
      <button className="btn btn-lg" onClick={go} disabled={busy}>
        <Icon name="lock" size="1.1rem" /> {busy ? "Waiting for your passkey…" : "Sign in with a passkey"}
      </button>
      {error && <p className="banner banner-error">{error}</p>}
    </>
  );
}

/** /join?t=… : a family member's invite. Saves a passkey on this phone and signs it in. */
export function JoinInvite() {
  const token = new URLSearchParams(window.location.search).get("t") ?? "";
  const info = useQuery({
    queryKey: ["invite", token],
    queryFn: () => api<{ member: { displayName: string; color: string }; expiresAt: string }>(`/auth/passkey/invite?t=${encodeURIComponent(token)}`),
    retry: false,
    staleTime: Infinity,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const { challengeId, options } = await api<{ challengeId: string; options: Parameters<typeof startRegistration>[0]["optionsJSON"] }>("/auth/passkey/register/options", { method: "POST", body: { token } });
      const response = await startRegistration({ optionsJSON: options });
      await api("/auth/passkey/register/verify", { method: "POST", body: { challengeId, response } });
      window.location.replace("/");
    } catch (e) {
      setError(passkeyError(e));
      setBusy(false);
    }
  };

  let body;
  if (info.isLoading) body = <p className="muted">Loading…</p>;
  else if (info.error || !info.data)
    body = (
      <>
        <p className="banner banner-error">{info.error?.message ?? "This invite isn’t valid."}</p>
        <a className="btn btn-lg" href="/">
          Go to Kinmantle
        </a>
      </>
    );
  else if (!browserSupportsWebAuthn())
    body = <p className="banner banner-error">This browser can’t save passkeys. Open the link in Safari on an iPhone or Chrome on Android.</p>;
  else
    body = (
      <>
        <Avatar member={{ displayName: info.data.member.displayName, color: info.data.member.color }} size="3.2rem" />
        <h2>Hi {info.data.member.displayName}!</h2>
        <p>Set up Kinmantle on this phone. Your phone saves a passkey, and from then on you sign in with your face, your fingerprint or your screen lock. No password.</p>
        <button className="btn btn-primary btn-lg" onClick={create} disabled={busy}>
          <Icon name="lock" size="1.1rem" /> {busy ? "Follow your phone’s prompt…" : "Create my passkey"}
        </button>
        {error && <p className="banner banner-error">{error}</p>}
        <p className="muted small">Lost this phone later? A parent can remove its passkey in Admin → Family.</p>
      </>
    );

  return (
    <div className="signin">
      <Scenery phase="dusk" condition="partly" season="autumn" moon={null} animate />
      <div className="signin-card">
        <Wordmark />
        {body}
      </div>
    </div>
  );
}
